import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useStore } from '../../contexts/StoreContext';
import { LedFrame } from '../ui/LedFrame';
import { isWithin } from '../../utils/format';
const AUTOPLAY = 3800;
const ease = [0.23, 1, 0.32, 1] as const;
export function BannerCarousel() {
  const {
    banners
  } = useStore();
  const active = useMemo(() => banners.filter(b => b.active && isWithin(b.startsAt, b.endsAt)), [banners]);
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || active.length < 2) return;
    const t = window.setTimeout(() => {
      setDir(1);
      setIndex(i => (i + 1) % active.length);
    }, AUTOPLAY);
    return () => window.clearTimeout(t);
  }, [paused, active.length, index]);
  useEffect(() => {
    if (index >= active.length) setIndex(0);
  }, [active.length, index]);
  if (active.length === 0) return null;
  const b = active[index] ?? active[0];
  const go = (to: number) => {
    setDir(to > index ? 1 : -1);
    setIndex((to + active.length) % active.length);
  };
  return <section className="container-x py-8" aria-roledescription="carrusel" aria-label="Destacados" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-611" data-mp="lquxg691czmv">
      <LedFrame radius={20} className="bg-ink" data-id="element-612" data-mp="1dgpllzl5qj9y">
        <div className="relative h-[440px] overflow-hidden rounded-[20px] md:h-[380px]" data-id="element-613" data-mp="1dgfx6gfrgk6d">
          <AnimatePresence initial={false} custom={dir} mode="popLayout" data-id="element-614" data-mp="zgeuqwjul4os">
            <motion.div key={b.id} className="absolute inset-0 grid md:grid-cols-[1fr_1.15fr]" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease: 'easeOut'
          }} data-id="element-615" data-mp="14fi24nnavmzx">
              <div className="relative z-10 order-2 flex flex-col justify-center p-6 sm:p-10 md:order-1" data-id="element-616" data-mp="3p7bgz7is7nnm">
                <motion.span initial={{
                opacity: 0,
                x: -12
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.25,
                ease,
                delay: 0.05
              }} className="w-fit rounded-md border border-neon/60 bg-neon/10 px-2.5 py-1 text-xs font-bold tracking-wide text-neon-hi" data-id="element-617" data-mp="353w2loe93raj">
                  {b.hook ?? b.kind}
                </motion.span>
                <motion.h2 initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.28,
                ease,
                delay: 0.1
              }} className="text-glow mt-3 font-display text-4xl font-black leading-none tracking-tight sm:text-5xl lg:text-6xl" data-id="element-618" data-mp="2rb6f1fj2qebb">
                  {b.title}
                </motion.h2>
                <motion.p initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.28,
                ease,
                delay: 0.15
              }} className="mt-3 max-w-md text-sm text-mist sm:text-base" data-id="element-619" data-mp="28e4x6u4f8jpy">
                  {b.subtitle}
                </motion.p>
                <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.28,
                ease,
                delay: 0.2
              }} data-id="element-620" data-mp="6o35evhsgvxo">
                  <Link to={b.link} className="btn btn-primary mt-6 w-fit" data-id="element-621" data-mp="j5e1djmxjc2y">
                    {b.ctaLabel}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-622" data-mp="nwckss37sqaf" />
                  </Link>
                </motion.div>
              </div>
              <div className="relative order-1 h-48 overflow-hidden md:order-2 md:h-full" data-id="element-623" data-mp="stvynqw0311u">
                <motion.img src={b.image} alt="" custom={dir} initial={{
                opacity: 0,
                x: dir * 40,
                scale: 1.04
              }} animate={{
                opacity: 1,
                x: 0,
                scale: 1
              }} transition={{
                duration: 0.3,
                ease
              }} className="h-full w-full object-cover saturate-[1.2] contrast-[1.08]" data-id="element-624" data-mp="3rdunkl44r73z" />
              </div>
            </motion.div>
          </AnimatePresence>

          {active.length > 1 && <div className="absolute right-4 top-4 z-20 flex gap-2" data-id="element-625" data-mp="1kks8sjqoerlf">
              <button onClick={() => go(index - 1)} className="rounded-full border border-neon/50 bg-ink/80 p-2 transition-colors duration-150 hover:border-neon-hi" aria-label="Anterior" data-id="element-626" data-mp="1j4ehjtea1oih">
                <ChevronLeftIcon className="h-4 w-4" data-id="element-627" data-mp="1t3wys22syw6u" />
              </button>
              <button onClick={() => go(index + 1)} className="rounded-full border border-neon/50 bg-ink/80 p-2 transition-colors duration-150 hover:border-neon-hi" aria-label="Siguiente" data-id="element-628" data-mp="1o4aljsmz5fy1">
                <ChevronRightIcon className="h-4 w-4" data-id="element-629" data-mp="221pe368iy3u8" />
              </button>
            </div>}
        </div>
      </LedFrame>

      {active.length > 1 && <div className="-mx-4 mt-4 overflow-x-auto px-4 sm:mx-0 sm:px-0" data-id="element-630" data-mp="2btby9s2ubd94">
          <div role="tablist" aria-label="Categorías destacadas" className="flex min-w-max gap-2" data-id="element-631" data-mp="26uijzxfqbi7w">
            {active.map((x, i) => <button key={x.id} role="tab" aria-selected={i === index} onClick={() => go(i)} className={`relative overflow-hidden rounded-full border px-4 py-2 text-xs font-bold tracking-wider transition-colors duration-150 ${i === index ? 'border-neon text-white shadow-[0_0_14px_rgba(20,99,255,0.6)]' : 'border-line text-mist hover:border-neon/50 hover:text-white'}`} data-id="element-632" data-mp="2kutav0h1gwt2" data-mpi={i}>
                {x.title}
                {i === index && !paused && <motion.span key={`p-${x.id}-${index}`} aria-hidden className="absolute bottom-0 left-0 h-0.5 bg-neon-hi" initial={{
            width: '0%'
          }} animate={{
            width: '100%'
          }} transition={{
            duration: AUTOPLAY / 1000,
            ease: 'linear'
          }} data-id="element-633" data-mp="2gr0sjsrzxysv" />}
              </button>)}
          </div>
        </div>}
    </section>;
}